import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export default function FeatureGrid() {
  const [isVisible, setIsVisible] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    const section = document.getElementById('funktionen');
    if (section) observer.observe(section);

    return () => observer.disconnect();
  }, []);

  const features = [
    {
      icon: 'ri-google-line',
      titleKey: 'features.feature1.title',
      descKey: 'features.feature1.desc',
      delay: '0ms'
    },
    {
      icon: 'ri-bar-chart-line',
      titleKey: 'features.feature2.title',
      descKey: 'features.feature2.desc',
      delay: '100ms'
    },
    {
      icon: 'ri-team-line',
      titleKey: 'features.feature3.title',
      descKey: 'features.feature3.desc',
      delay: '200ms'
    },
    {
      icon: 'ri-notification-3-line',
      titleKey: 'features.feature4.title',
      descKey: 'features.feature4.desc',
      delay: '300ms'
    },
    {
      icon: 'ri-shield-check-line',
      titleKey: 'features.feature5.title',
      descKey: 'features.feature5.desc',
      delay: '400ms'
    },
    {
      icon: 'ri-customer-service-line',
      titleKey: 'features.feature6.title',
      descKey: 'features.feature6.desc',
      delay: '500ms'
    }
  ];

  return (
    <section id="funktionen" className="relative py-32 bg-white overflow-hidden border-t border-slate-100">
      {/* Background Elements */}
      <div className="absolute top-20 left-10 w-96 h-96 bg-gradient-to-r from-[#10B981] to-[#1E3A8A] rounded-full blur-[120px]"></div>
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-emerald-50 rounded-full blur-[120px]"></div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-[url('/images/grid.svg')] opacity-[0.03] pointer-events-none"></div>

      <div className="max-w-[1280px] mx-auto px-6 relative z-10">
        <div className={`text-center mb-20 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/50 backdrop-blur-sm rounded-full mb-6 border border-blue-200 shadow-sm">
            <i className="ri-flashlight-line text-[#10B981]"></i>
            <span className="text-sm font-medium text-transparent bg-clip-text bg-gradient-to-r from-[#10B981] to-[#1E3A8A]" style={{ fontFamily: 'DM Sans, sans-serif' }}>
              {t('features.badge')}
            </span>
          </div>

          <h2 className="text-5xl md:text-6xl font-bold text-[#0F172A] mb-6 leading-tight" style={{ fontFamily: 'Inter Tight, sans-serif' }}>
            {t('features.title')} <span className="bg-gradient-to-r from-[#10B981] to-[#1E3A8A] bg-clip-text text-transparent animate-pulse">{t('features.titleHighlight')}</span>
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed" style={{ fontFamily: 'DM Sans, sans-serif' }}>
            {t('features.subtitle')}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, idx) => (
            <div
              key={idx}
              className={`group bg-white rounded-2xl p-8 border border-slate-200 hover:border-emerald-300 hover:shadow-lg hover:-translate-y-1 transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: isVisible ? feature.delay : '0ms' }}
            >
              <div className="w-14 h-14 bg-gradient-to-br from-[#10B981] to-[#1E3A8A] rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 border border-blue-100 group-hover:border-white/30">
                <i className={`${feature.icon} text-3xl text-white`}></i>
              </div>
              <h3 className="text-xl font-bold text-[#0F172A] mb-3 group-hover:text-[#10B981] transition-colors" style={{ fontFamily: 'Inter Tight, sans-serif' }}>
                {t(feature.titleKey)}
              </h3>
              <p className="text-slate-600 leading-relaxed transition-colors" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                {t(feature.descKey)}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
